import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { paymentStatus } from "@/lib/api";
import { useLang } from "@/i18n";

export default function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sid) { setState("failed"); return; }
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 20 && !cancelled; i++) {
        try {
          const s = await paymentStatus(sid);
          if (s.payment_status === "paid") {
            setState("paid");
            toast.success(t(s.plan === "monthly" ? "Abbonamento attivato!" : "PDF sbloccato!"));
            setTimeout(() => navigate(s.trip_id ? `/viaggio/${s.trip_id}` : "/account"), 1500);
            return;
          }
          if (["expired", "failed"].includes(s.payment_status)) { setState("failed"); return; }
        } catch { /* retry */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setState("timeout");
    })();
    return () => { cancelled = true; };
  }, [sid, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const view = {
    checking: [Loader2, "Stiamo verificando il pagamento...", "animate-spin text-emerald-600"],
    paid: [CheckCircle2, "Pagamento completato! Ti stiamo riportando al tuo itinerario.", "text-emerald-600"],
    failed: [XCircle, "Pagamento non riuscito o annullato.", "text-red-500"],
    timeout: [Loader2, "La verifica sta richiedendo più tempo del previsto. Controlla tra poco in 'I miei viaggi'.", "text-amber-500"],
  }[state];
  const [Icon, text, cls] = view;

  return (
    <PageShell testId="payment-success-page" eyebrow={t("Pagamento")} title={t("Grazie per il tuo acquisto")}>
      <div data-testid={`payment-status-${state}`} className="flex items-center gap-4 rounded-3xl border border-emerald-100 bg-white p-8">
        <Icon className={`h-8 w-8 shrink-0 ${cls}`} />
        <p className="font-display text-lg font-semibold text-gray-900">{t(text)}</p>
      </div>
      {state !== "checking" && state !== "paid" && (
        <Link to="/account" data-testid="payment-go-account" className="mt-6 inline-block rounded-full bg-emerald-600 px-6 py-3 font-semibold text-white">{t("Vai a I miei viaggi")}</Link>
      )}
    </PageShell>
  );
}
